/**
 * One route shape — `/<article>` — and a back button that works.
 *
 * `BASE_URL` is what lets the manual sit at the root of `docs.tryairlock.app` or
 * under `/docs` of one domain without either being a special case: build with
 * `--base=/docs/` and every link and every read of the address bar moves with
 * it.
 */
import { useCallback, useEffect, useState } from 'react';

const BASE = import.meta.env.BASE_URL.replace(/\/+$/, '');

/** The chapter id in the address bar, or '' for the table of contents. */
function readId(): string {
  const path = window.location.pathname;
  const rest = BASE && path.startsWith(BASE) ? path.slice(BASE.length) : path;
  return rest.replace(/^\/+|\/+$/g, '');
}

/**
 * The manual's front page is its first chapter, and it has one address.
 *
 * `/` and `/welcome` would otherwise be the same words at two URLs — the thing
 * a crawler is entitled to complain about, and a link somebody sends that does
 * not match the one in their address bar. Chapter one is written as `/`
 * wherever it is linked, and `useDocRoute` rewrites the other way in.
 */
export function hrefFor(id: string): string {
  return id === FRONT ? `${BASE}/` : `${BASE}/${id}`;
}

/** The chapter the front page shows. The contents move to a page of their own. */
export const FRONT = 'welcome';
export const CONTENTS = 'contents';

/** Where this manual is published, for the canonical each article names. */
const SITE = 'https://docs.tryairlock.app';

/**
 * The three tags a crawler and a browser tab read, per article.
 *
 * `index.html` carries one static title and one static description, so every
 * page of the manual presented a crawler with identical metadata — forty
 * chapters indistinguishable from one another, and a shared link that says
 * nothing about what it points at. The site's own router has done this from the
 * start (`site/src/lib/router.ts`); this is the same thing, one deployment over.
 */
export function useDocHead({ title, description, id }: { title: string; description: string; id: string }): void {
  useEffect(() => {
    document.title = `${title} — Airlock documentation`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta && description) meta.setAttribute('content', description);
    const canonical = `${SITE}${id && id !== FRONT ? `/${id}` : ''}`;
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonical);
  }, [title, description, id]);
}

export function useDocRoute(): { id: string; go: (id: string) => void } {
  const [id, setId] = useState(readId);

  // `/welcome` typed or followed from an old link lands on the same chapter at
  // the address it is published under.
  useEffect(() => {
    if (id !== FRONT) return;
    window.history.replaceState(null, '', hrefFor(FRONT));
  }, [id]);

  useEffect(() => {
    const onPop = (): void => setId(readId());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const go = useCallback((next: string): void => {
    window.history.pushState(null, '', hrefFor(next));
    setId(next);
    window.scrollTo(0, 0);
  }, []);

  return { id, go };
}
